/**
 * Canonical media_type resolver for a post's media_urls list.
 *
 * Authored to the PUBLISHER's rule (the source of truth for what actually
 * ships): `supabase/functions/_shared/post-dispatcher.ts` inferMediaType.
 *   1. >1 url → carousel, ALWAYS — including a video child. Rule (a) of the
 *      2026-07-11 preview↔publish coherence fix: the IG publisher's >1-media
 *      branch ships a CAROUSEL container regardless of placement, so a
 *      video+image post previewing as a Reel was a silent lie. Carousels may
 *      contain video children; a multi-media post IS a carousel everywhere.
 *   2. single video url → video.
 *   3. otherwise → image.
 *
 * Publisher source (post-dispatcher.ts:92) uses /\.(mp4|mov|webm)(\?|$)/i and
 * checks only mediaUrls[0] (first url). This canonical helper extends the
 * extension set with .m4v (per the design contract) and uses Array.some() so
 * that a video url anywhere in a multi-url list wins (video-before-carousel).
 * Both divergences are intentional: the design contract explicitly calls them
 * out so the three write surfaces (PostBody.handleMediaChange, the
 * /api/generate-image write-back, and the dispatcher) never diverge on format.
 *
 * Unlike the dispatcher (which returns 'text' for an empty list), this
 * helper's MediaType union has no 'text'. An empty list falls through to
 * 'image' as a safe, never-shipped default; callers that need to skip the
 * write on empty should check mediaUrls.length before calling.
 */
export type MediaType = 'image' | 'video' | 'carousel';

const VIDEO_RE = /\.(mp4|mov|webm|m4v)(\?|$)/i;

export function inferMediaType(mediaUrls: string[]): MediaType {
  if (mediaUrls.length > 1) return 'carousel';
  if (mediaUrls.some((url) => VIDEO_RE.test(url))) return 'video';
  return 'image';
}
